import { fileCategory, type FileCategory } from '@xfile/shared';

/**
 * Icônes de TYPE DE FICHIER (XFILE-07 UI) — SVG inline COLORÉS par catégorie.
 *
 * DÉROGATION ASSUMÉE à la règle « Heroicons outline seulement » de CLAUDE.md, limitée
 * à la reconnaissance visuelle des types de fichiers (PDF, Word, Excel, PowerPoint,
 * image, vidéo, audio, archive, texte…). Le reste du chrome reste en Heroicons. La
 * catégorie est calculée par le helper PUR partagé `fileCategory(mime, name)` — source
 * unique front + back — jamais dupliqué ici.
 *
 * Toutes les icônes sont DÉCORATIVES (`aria-hidden`) : le sens est porté par le nom du
 * fichier adjacent (a11y — jamais l'information par la couleur seule). L'attribut
 * `data-category` expose la catégorie choisie (diagnostic + tests).
 */

/** Couleur d'accent par catégorie (hex assumé — cf. dérogation icônes de type). */
const CATEGORY_COLOR: Record<FileCategory, string> = {
  pdf: '#DC2626',
  word: '#2563EB',
  excel: '#16A34A',
  powerpoint: '#EA580C',
  text: '#64748B',
  archive: '#CA8A04',
  image: '#0D9488',
  video: '#7C3AED',
  audio: '#DB2777',
  other: '#6B7280',
};

/** Étiquette courte affichée sur les documents « bureautiques ». */
const CATEGORY_LABEL: Partial<Record<FileCategory, string>> = {
  pdf: 'PDF',
  word: 'DOC',
  excel: 'XLS',
  powerpoint: 'PPT',
  text: 'TXT',
};

interface FileTypeIconProps {
  mimeType: string;
  name?: string;
  className?: string;
}

/** Contour de page (coin plié) commun à toutes les icônes de document. */
function Page({ color }: { color: string }): React.JSX.Element {
  return (
    <>
      <path
        d="M6 2.5h7.2L18 7.3V20a1.5 1.5 0 0 1-1.5 1.5h-10A1.5 1.5 0 0 1 5 20V4a1.5 1.5 0 0 1 1.5-1.5Z"
        fill={color}
        fillOpacity="0.12"
        stroke={color}
        strokeWidth="1.3"
        strokeLinejoin="round"
      />
      <path
        d="M13 2.5V6a1.5 1.5 0 0 0 1.5 1.5H18"
        fill="none"
        stroke={color}
        strokeWidth="1.3"
        strokeLinejoin="round"
      />
    </>
  );
}

/** Bandeau d'étiquette (texte blanc sur fond coloré) pour les documents bureautiques. */
function LabelBand({ color, label }: { color: string; label: string }): React.JSX.Element {
  return (
    <>
      <rect x="2.5" y="12.5" width="13" height="6.5" rx="1.4" fill={color} />
      <text
        x="9"
        y="17.4"
        textAnchor="middle"
        fontSize="4.4"
        fontWeight="700"
        fill="#ffffff"
        fontFamily="ui-sans-serif, system-ui, sans-serif"
      >
        {label}
      </text>
    </>
  );
}

/** Rendu du glyphe interne propre à la catégorie (par-dessus la page). */
function Glyph({ category, color }: { category: FileCategory; color: string }): React.JSX.Element {
  const label = CATEGORY_LABEL[category];
  if (label !== undefined) {
    return <LabelBand color={color} label={label} />;
  }
  switch (category) {
    case 'image':
      return (
        <>
          <circle cx="9" cy="12" r="1.4" fill={color} />
          <path
            d="M6 18.5 9.5 14l2.2 2.6L14 13.5l3 5"
            fill="none"
            stroke={color}
            strokeWidth="1.3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </>
      );
    case 'video':
      return <path d="M8.5 12.5v5l4.5-2.5-4.5-2.5Z" fill={color} stroke={color} strokeWidth="1" strokeLinejoin="round" />;
    case 'audio':
      return (
        <>
          <path
            d="M14.5 11.5 10 12.8V17"
            fill="none"
            stroke={color}
            strokeWidth="1.3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <circle cx="9" cy="17.3" r="1.3" fill={color} />
          <circle cx="14.5" cy="16.1" r="1.3" fill={color} />
        </>
      );
    case 'archive':
      return (
        <>
          <path
            d="M11 9v9"
            fill="none"
            stroke={color}
            strokeWidth="1.3"
            strokeDasharray="1.4 1.2"
            strokeLinecap="round"
          />
          <rect x="9.6" y="14.5" width="2.8" height="3.4" rx="0.6" fill={color} />
        </>
      );
    default:
      return (
        <>
          <path
            d="M8 12.5h6M8 15h6M8 17.5h3.5"
            fill="none"
            stroke={color}
            strokeWidth="1.3"
            strokeLinecap="round"
          />
        </>
      );
  }
}

/**
 * Icône reconnaissable du type d'un fichier, choisie via `fileCategory(mimeType, name)`.
 * `className` par défaut `size-5` (aligné sur les lignes de fichier de l'explorateur).
 */
export function FileTypeIcon({
  mimeType,
  name,
  className = 'size-5',
}: FileTypeIconProps): React.JSX.Element {
  const category = fileCategory(mimeType, name);
  const color = CATEGORY_COLOR[category];
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      className={className}
      data-category={category}
      aria-hidden="true"
    >
      <Page color={color} />
      <Glyph category={category} color={color} />
    </svg>
  );
}
